/**
 * ADDAPT Client-Side Metrics Utility
 *
 * Lightweight metrics collection for frontend KPIs.
 * Uses navigator.sendBeacon for fire-and-forget delivery to avoid
 * blocking the UI thread. Falls back to fetch for browsers without beacon support.
 *
 * Metrics are batched and flushed periodically or on page unload.
 */

interface MetricEntry {
  name: string;
  value: number;
  unit: 'ms' | 'count' | 'percent';
  dimensions?: Record<string, string>;
  timestamp: number;
}

const BATCH_SIZE = 10;
const FLUSH_INTERVAL_MS = 30_000; // 30 seconds
let metricBuffer: MetricEntry[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Flush buffered metrics to the backend via WebSocket.
 * Falls back to console logging if WebSocket is not available.
 */
function flushMetrics(): void {
  if (metricBuffer.length === 0) return;

  const batch = [...metricBuffer];
  metricBuffer = [];

  // Try to send via the existing WebSocket connection (set by websocket.service.ts)
  const sendRaw = (window as any).__ADDAPT_WS_SEND_RAW__;
  if (typeof sendRaw === 'function') {
    try {
      sendRaw({
        action: 'reportMetrics',
        metrics: batch,
      });
      return;
    } catch (_err) {
      // WebSocket send failed — fall through to console
    }
  }

  // Fallback: log to console for dev visibility
  if (process.env.NODE_ENV === 'development') {
    console.debug('[ADDAPT Metrics]', batch);
  }
}

/**
 * Start the periodic flush timer.
 */
function ensureFlushTimer(): void {
  if (flushTimer) return;
  flushTimer = setInterval(flushMetrics, FLUSH_INTERVAL_MS);

  // Flush on page unload
  if (typeof window !== 'undefined') {
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flushMetrics();
      }
    });
  }
}

/**
 * Record a metric. Buffered and flushed periodically.
 */
function recordMetric(
  name: string,
  value: number,
  unit: 'ms' | 'count' | 'percent',
  dimensions?: Record<string, string>
): void {
  ensureFlushTimer();

  metricBuffer.push({
    name,
    value,
    unit,
    dimensions,
    timestamp: Date.now(),
  });

  if (metricBuffer.length >= BATCH_SIZE) {
    flushMetrics();
  }
}

// ─── P0: Page Load Time ───

/**
 * Measure and record page load time using Performance API.
 * Call once from App component after mount.
 */
export function recordPageLoadTime(): void {
  // Use PerformanceNavigationTiming if available (modern browsers)
  if (typeof window !== 'undefined' && window.performance && typeof window.performance.getEntriesByType === 'function') {
    // Wait for load event to complete
    const measure = () => {
      const entries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      if (entries.length > 0) {
        const nav = entries[0];
        const loadTime = nav.loadEventEnd - nav.startTime;
        if (loadTime > 0) {
          recordMetric('PageLoadTime', Math.round(loadTime), 'ms');
          return;
        }
      }
      // Fallback: use performance.now() as approximate load time
      recordMetric('PageLoadTime', Math.round(performance.now()), 'ms');
    };

    if (document.readyState === 'complete') {
      // Already loaded — measure immediately (next tick to get loadEventEnd)
      setTimeout(measure, 0);
    } else {
      window.addEventListener('load', () => setTimeout(measure, 0));
    }
  }
}

// ─── P0: Chatbot Response Time (user-perceived) ───

const pendingMessages = new Map<string, number>();

/**
 * Mark the start of a chat message send. Call when user submits a message.
 * @returns messageKey to pass to recordChatResponseEnd
 */
export function recordChatMessageSent(sessionId: string): string {
  const key = `${sessionId}_${Date.now()}`;
  pendingMessages.set(key, Date.now());
  return key;
}

/**
 * Mark the end of a chat response (agentEnd received).
 * Records the full round-trip latency.
 */
export function recordChatResponseEnd(messageKey: string): void {
  const startTime = pendingMessages.get(messageKey);
  if (startTime) {
    const latency = Date.now() - startTime;
    recordMetric('ChatResponseTime', latency, 'ms');
    pendingMessages.delete(messageKey);
  }
}

// ─── WebSocket Connect Latency ───

let wsConnectStart: number | null = null;

/**
 * Mark the start of a WebSocket connection attempt.
 */
export function recordWsConnectStart(): void {
  wsConnectStart = Date.now();
}

/**
 * Mark successful WebSocket connection. Records connect latency.
 */
export function recordWsConnectSuccess(): void {
  if (wsConnectStart !== null) {
    const latency = Date.now() - wsConnectStart;
    recordMetric('WebSocketConnectLatency', latency, 'ms');
    wsConnectStart = null;
  }
}

/**
 * Record a WebSocket connection failure.
 */
export function recordWsConnectFailure(reason: string): void {
  recordMetric('WebSocketConnectFailure', 1, 'count', { reason });
  wsConnectStart = null;
}

// ─── P0: Session Failure (client-side) ───

/**
 * Record a client-side session failure (unhandled WS error, timeout, etc.)
 */
export function recordSessionFailureClient(sessionId: string, errorType: string): void {
  recordMetric('SessionFailureClient', 1, 'count', { errorType });
}

// ─── P1: CSAT Rating ───

/**
 * Record a user satisfaction rating.
 */
export function recordCSATRating(sessionId: string, rating: number): void {
  recordMetric('CSATRating', rating, 'count');
}
